import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import examples from "libphonenumber-js/examples.mobile.json";
import { useTranslation } from "react-i18next";

import useToast from "../../components/useToast";
import { API_BASE_URL } from "../../config";
import AppCallout from "../../design-system/components/AppCallout";
import AppCard from "../../design-system/components/AppCard";
import AppCardBody from "../../design-system/components/AppCardBody";
import AppCardHeader from "../../design-system/components/AppCardHeader";
import AppEditActions from "../../design-system/components/AppEditActions";
import AppField from "../../design-system/components/AppField";
import AppFieldGrid from "../../design-system/components/AppFieldGrid";
import AppInput from "../../design-system/components/AppInput";
import AppLoadingOverlay from "../../design-system/components/AppLoadingOverlay";
import AppSelect from "../../design-system/components/AppSelect";
import CountryFlag from "../../design-system/components/CountryFlag";
import { IconLock, IconProfile } from "../../design-system/icons";
import { hasAppValueChanged } from "../../design-system/utils/appValueEquality";
import "../../design-system/styles/tokens.css";
import "../../design-system/styles/components.css";
import { isEmployeeAccessLevel } from "../../utils/accessLevels";

const e164ToWhatsApp00 = (e164) => (e164 ? e164.replace(/^\+/, "00") : null);

const backend00ToE164 = (value) => {
  if (!value) return "";
  const cleaned = String(value).trim().replace(/\s+/g, "");
  return cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
};

const hasOnlyAllowedPhoneChars = (value) => {
  if (!value) return true;
  if (!/^[0-9+().\-\s]*$/.test(value)) return false;
  const plusCount = (value.match(/\+/g) || []).length;
  if (plusCount > 1) return false;
  return plusCount === 0 || value.trim().startsWith("+");
};

const normalizeToE164 = (raw, defaultCountry = "CH") => {
  if (!raw) return null;
  let value = String(raw).trim();
  if (!hasOnlyAllowedPhoneChars(value)) return null;
  value = value.replace(/[^\d+]/g, "");
  if (value.startsWith("00")) value = `+${value.slice(2)}`;

  try {
    const phone = parsePhoneNumberFromString(value, defaultCountry);
    return phone?.isValid() ? phone.number : null;
  } catch {
    return null;
  }
};

const inferCountryFromE164 = (e164, fallback = "CH") => {
  if (!e164) return fallback;
  try {
    return parsePhoneNumberFromString(e164)?.country || fallback;
  } catch {
    return fallback;
  }
};

const backendPhoneToForm = (backendPhone, fallbackCountry = "CH") => {
  const e164 = backend00ToE164(backendPhone || "");
  const country = inferCountryFromE164(e164, fallbackCountry);
  try {
    const phone = e164 ? parsePhoneNumberFromString(e164, country) : null;
    return {
      phone_country: country,
      phone_number: phone ? phone.formatInternational() : e164,
    };
  } catch {
    return { phone_country: fallbackCountry, phone_number: e164 };
  }
};

const buildPhoneHint = (country = "CH") => {
  try {
    const example = getExampleNumber(country, examples);
    if (!example) {
      const code = getCountryCallingCode(country);
      return { placeholder: `+${code} ...`, hint: `+${code} ...` };
    }
    return {
      placeholder: example.formatInternational(),
      hint: `${example.formatInternational()} / ${example.formatNational()}`,
    };
  } catch {
    const code = getCountryCallingCode(country);
    return { placeholder: `+${code} ...`, hint: `+${code} ...` };
  }
};

const ProfileCard = ({
  icon,
  title,
  isEditing,
  onEdit,
  onCancel,
  onSave,
  saving,
  dirty,
  bodyInset = "default",
  children,
}) => {
  const { t } = useTranslation();
  return <AppCard>
    <AppCardHeader
      title={title}
      visual={icon}
      action={
        <AppEditActions
          editing={isEditing}
          dirty={dirty}
          saving={saving}
          onEdit={onEdit}
          onCancel={onCancel}
          onSave={onSave}
          editLabel={t("profile_settings.edit_named", { name: title })}
          cancelLabel={t("profile_settings.cancel_edit")}
          saveLabel={t("profile_settings.save_changes")}
          savingLabel={t("profile_settings.saving")}
          editVariant="neutral"
        />
      }
    />
    <AppCardBody inset={bodyInset}>
      {children}
    </AppCardBody>
  </AppCard>;
};

const ReadField = ({ label, value }) => (
  <AppField label={label}>
    <AppInput value={value || "-"} readOnly />
  </AppField>
);

export default function ProfileSettings() {
  const { t } = useTranslation();
  const token = localStorage.getItem("session_token");
  const { success, error } = useToast();

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [initialForm, setInitialForm] = useState({});
  const [formError, setFormError] = useState("");
  // Vor- und Nachname pflegt die Planung (Team-Verwaltung); das Backend
  // ignoriert sie fuer Mitarbeitende ohnehin.
  const canEditName = !isEmployeeAccessLevel(profile?.access_level);

  const countryOptions = useMemo(
    () =>
      getCountries().map((code) => {
        const callingCode = getCountryCallingCode(code);
        return {
          value: code,
          label: `${code} +${callingCode}`,
          displayLabel: `${code} +${callingCode}`,
          leadingVisual: <CountryFlag countryCode={code} />,
        };
      }),
    []
  );

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/api/web/profile`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProfile(res.data || {});
    } catch {
      error(t("profile_settings.errors.load"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startEditing = (name) => {
    setFormError("");
    if (name === "profile") {
      const phone = backendPhoneToForm(profile?.phone_number, "CH");
      const nextForm = {
        first_name: profile?.first_name || "",
        last_name: profile?.last_name || "",
        email: profile?.email || "",
        phone_country: phone.phone_country,
        phone_number: phone.phone_number,
      };
      setForm(nextForm);
      setInitialForm(nextForm);
    }
    if (name === "password") {
      const nextForm = { password: "", password2: "" };
      setForm(nextForm);
      setInitialForm(nextForm);
    }
    setEditing(name);
  };

  const cancelEditing = () => {
    setEditing(null);
    setForm({});
    setInitialForm({});
    setFormError("");
  };

  const saveProfile = async () => {
    if (!hasAppValueChanged(initialForm, form)) return;
    const firstName = String(form.first_name || "").trim();
    const lastName = String(form.last_name || "").trim();
    const email = String(form.email || "").trim();
    if (!email || (canEditName && (!firstName || !lastName))) {
      setFormError(t("profile_settings.errors.required"));
      return;
    }

    setSaving(true);
    setFormError("");
    try {
      const phoneE164 = normalizeToE164(form.phone_number, form.phone_country || "CH");
      await axios.put(
        `${API_BASE_URL}/api/web/profile`,
        {
          first_name: firstName,
          last_name: lastName,
          email,
          phone_number: phoneE164 ? e164ToWhatsApp00(phoneE164) : "",
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      success(t("profile_settings.messages.profile_saved"));
      cancelEditing();
      await fetchProfile();
    } catch (err) {
      setFormError(err?.response?.data?.message || t("profile_settings.errors.save_profile"));
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (!hasAppValueChanged(initialForm, form)) return;
    setSaving(true);
    setFormError("");
    try {
      await axios.put(
        `${API_BASE_URL}/api/web/profile/password`,
        { password: form.password, password2: form.password2 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      success(t("profile_settings.messages.password_saved"));
      cancelEditing();
    } catch (err) {
      setFormError(err?.response?.data?.message || t("profile_settings.errors.save_password"));
    } finally {
      setSaving(false);
    }
  };

  const phoneHint = buildPhoneHint(form.phone_country || "CH");

  const profileContent = (
    <>
      <AppLoadingOverlay open={loading} />
        <ProfileCard
          title={t("profile_settings.personal_data")}
          icon={<IconProfile />}
          isEditing={editing === "profile"}
          onEdit={() => startEditing("profile")}
          onCancel={cancelEditing}
          onSave={saveProfile}
          saving={saving}
          dirty={hasAppValueChanged(initialForm, form)}
        >
          {editing === "profile" ? (
            <AppFieldGrid columns={1}>
              {formError ? <AppCallout variant="error" description={formError} /> : null}
              <AppFieldGrid columns={2}>
                <AppField
                  label={t("profile_settings.first_name")}
                  required={canEditName}
                  help={canEditName ? undefined : t("profile_settings.name_managed_by_admin")}
                >
                  <AppInput
                    required={canEditName}
                    readOnly={!canEditName}
                    value={form.first_name || ""}
                    onChange={(event) => setForm((current) => ({ ...current, first_name: event.target.value }))}
                  />
                </AppField>
                <AppField label={t("profile_settings.last_name")} required={canEditName}>
                  <AppInput
                    required={canEditName}
                    readOnly={!canEditName}
                    value={form.last_name || ""}
                    onChange={(event) => setForm((current) => ({ ...current, last_name: event.target.value }))}
                  />
                </AppField>
                <AppField label={t("profile_settings.email")} required>
                  <AppInput
                    type="email"
                    required
                    value={form.email || ""}
                    onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                  />
                </AppField>
                <AppFieldGrid
                  columns={2}
                  style={{ gridTemplateColumns: "134.4px minmax(0, 1fr)" }}
                >
                  <AppField label={t("profile_settings.calling_code")}>
                    <AppSelect
                      options={countryOptions}
                      value={form.phone_country || "CH"}
                      searchable
                      searchPlaceholder={t("profile_settings.search_country")}
                      onChange={(event) => setForm((current) => ({
                        ...current,
                        phone_country: event.target.value || "CH",
                      }))}
                    />
                  </AppField>
                  <AppField label={t("profile_settings.phone")} help={t("profile_settings.phone_example", { example: phoneHint.hint })}>
                    <AppInput
                      value={form.phone_number || ""}
                      onChange={(event) => setForm((current) => ({ ...current, phone_number: event.target.value }))}
                      onBlur={() => {
                        const e164 = normalizeToE164(form.phone_number, form.phone_country || "CH");
                        if (!e164) return;
                        const phone = parsePhoneNumberFromString(e164);
                        if (phone) {
                          setForm((current) => ({ ...current, phone_number: phone.formatInternational() }));
                        }
                      }}
                      placeholder={phoneHint.placeholder}
                    />
                  </AppField>
                </AppFieldGrid>
              </AppFieldGrid>
            </AppFieldGrid>
          ) : (
            <AppFieldGrid columns={2}>
              <ReadField label={t("profile_settings.first_name")} value={profile?.first_name} />
              <ReadField label={t("profile_settings.last_name")} value={profile?.last_name} />
              <ReadField label={t("profile_settings.email")} value={profile?.email} />
              <ReadField label={t("profile_settings.phone")} value={backendPhoneToForm(profile?.phone_number).phone_number} />
            </AppFieldGrid>
          )}
        </ProfileCard>

        <ProfileCard
          title={t("profile_settings.password")}
          icon={<IconLock />}
          isEditing={editing === "password"}
          onEdit={() => startEditing("password")}
          onCancel={cancelEditing}
          onSave={savePassword}
          saving={saving}
          dirty={hasAppValueChanged(initialForm, form)}
          bodyInset={editing === "password" ? "default" : "none"}
        >
          {editing === "password" ? (
            <AppFieldGrid columns={1}>
              {formError ? <AppCallout variant="error" description={formError} /> : null}
              <AppField label={t("profile_settings.new_password")}>
                <AppInput
                  type="password"
                  value={form.password || ""}
                  onChange={(event) => setForm((current) => ({ ...current, password: event.target.value }))}
                  autoComplete="new-password"
                />
              </AppField>
              <AppField label={t("profile_settings.confirm_password")}>
                <AppInput
                  type="password"
                  value={form.password2 || ""}
                  onChange={(event) => setForm((current) => ({ ...current, password2: event.target.value }))}
                  autoComplete="new-password"
                />
              </AppField>
            </AppFieldGrid>
          ) : (
            <AppCallout
              variant="info"
              title={t("profile_settings.password_protected")}
              description={t("profile_settings.password_protected_help")}
              icon={<IconLock />}
            />
          )}
        </ProfileCard>
    </>
  );

  return profileContent;
}
